/**
 * Lane C4 (skeleton): the unarmed / staff arts' sample-style effects from the swordKit primitives (no shaders, no new
 * pen code), the sibling of swordArtGen. `makeHandArt` turns an art's palette + motif + a skill -> family table into
 * `ArtFx` ports. Families: palm (print + ground shockwave; reach >= 4 dashes in), fist (burst), claw (three rakes),
 * grab (lock brackets), throw (small streaking darts, wind trail, no beam), ring (ground ring), power (set piece),
 * buff, heal. Moving skills leave split-body afterimages. Skeleton quality: polish later.
 */
import { add, diamond, hash, isoRing, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { bladeArc, C, clamp01, fade, hitStar, isoDisc, level, puffs, ringOn, since, strikeAt, tilesPx, windPass, type ArtFx } from './swordKit';
import { motif, type Pal } from './swordArtGen';

export type HandFamily = 'palm' | 'fist' | 'claw' | 'grab' | 'throw' | 'ring' | 'power' | 'buff' | 'heal';
export type HandRow = readonly [id: string, family: HandFamily, reach?: number];
type Wpn = 'none' | 'staff';

const dashMove = (len: number, start: number) => (s: Scene, t: number): V => {
  const k0 = clamp01((t - start) / 0.24);
  const k = 1 - (1 - k0) * (1 - k0);
  const back = 1 - clamp01((t - (start + 1.5)) / 0.3);
  return [s.d[0] * len * k * back, s.d[1] * len * k * back];
};

const NOTE: Record<HandFamily, string> = {
  palm: 'ฝ่ามือรวมแสงที่ฝ่ามือ -> พิมพ์ฝ่ามือกระแทก คลื่นพื้นขยาย ลมพัดผ่านเป้า -> ลายวิชา -> จาง',
  fist: 'กำหมัดรวมแรง -> หมัดกระแทกแตกเป็นแฉก ฝุ่นและลายวิชากระจาย -> จาง',
  claw: 'ประกายปลายนิ้ว -> สามรอยข่วนขนานบนเป้า เงาร่างแยกตามหลัง -> ลายวิชา',
  grab: 'พุ่งเข้าจับ เงาร่างแยก -> วงล็อกสี่มุมรัดเป้า -> ดาวกระแทก -> จาง',
  throw: 'ซัดอาวุธลับเล็กเป็นพัด -> เส้นลมสั้นพุ่งผ่านเป้า (ไม่มีลำแสง) -> ปักเป็นประกาย -> จาง',
  ring: 'วงพื้นกำหนดเขต -> กระแทกพื้น คลื่นสองระลอก ลายวิชากระจายที่ขอบวง',
  power: 'รวมพลังเป็นวงที่เท้า -> ฝ่ามือหนักเงาร่างแยกสามตัว -> คลื่นพื้นและลายวิชากระจายกว้าง',
  buff: 'วงแหวนลอยขึ้นรอบตัวสามชั้น -> ลายวิชาลอยตาม -> จางหาย',
  heal: 'ประกายลอยขึ้นจากพื้น วงแหวนอ่อนรอบตัว -> จางหาย',
};

function handGlow(g: G, s: Scene, t0: number, t1: number, P: Pal): void {
  if (s.t < t0 || s.t >= t1) return;
  const { hand } = s.limbs(s.t);
  const r = s.t < (t0 + t1) / 2 ? 1 : 2;
  level(g, 3);
  diamond(g, hand[0], hand[1], r, C(P.a, 5), C('white', 8));
  g.globalAlpha = 1;
}

function landing(g: G, s: Scene, at: number, P: Pal, seed: number, big = 1, wind = true): void {
  const e = since(s.t, at);
  const c = strikeAt(s);
  hitStar(g, s, c, e, P.a, big);
  motif(g, c, e - 1, P, seed, 6 + Math.round(big * 3), big);
  if (wind && e >= 0) windPass(g, add(c, s.d, -4), [s.d[0], s.d[1]], e, { n: 5, speed: 10, len: 14, spread: 12, life: 8, ramp: P.a, seed });
}

function ringBurst(g: G, s: Scene, hit: number, rx: number, P: Pal, seed: number, at: V = s.a): void {
  const tell = since(s.t, hit - 0.4);
  if (s.t < hit) {
    level(g, 3);
    ringOn(g, at, rx, -Math.PI / 2, clamp01(tell / 4), C(P.a, 4), true);
    g.globalAlpha = 1;
    return;
  }
  const e = since(s.t, hit);
  if (e > 12) return;
  if (e < 3) isoDisc(g, at, rx * (0.45 + e * 0.2), C(P.b, 3), C(P.a, 5), e < 1 ? 2 : 1);
  for (let k = 0; k < 2; k++) {
    const ee = e - k * 2;
    if (ee < 0 || ee > 8) continue;
    level(g, fade(ee, 3, 6));
    isoRing(g, at[0], at[1], Math.round(rx * (0.5 + 0.5 * clamp01(ee / 5))), k ? C(P.b, 5) : C(P.a, 6), k === 0);
  }
  g.globalAlpha = 1;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + hash(i, seed) * 0.3;
    motif(g, [at[0] + Math.cos(a) * rx * 0.85, at[1] + Math.sin(a) * rx * 0.43], e - 1, P, seed + i, 2, 0.7, 8);
  }
}

/** Small darts fanning from the hand to the foe, each a short wind streak (no beam). */
function darts(g: G, s: Scene, t0: number, t1: number, P: Pal, seed: number, n: number): void {
  if (s.t < t0) return;
  const { hand } = s.limbs(t0);
  const dst = strikeAt(s);
  const e = (s.t - t0) / (t1 - t0);
  for (let i = 0; i < n; i++) {
    const k = clamp01(e - i * 0.03);
    if (k <= 0) continue;
    if (k >= 1) {
      if (e < 1.4) px(g, Math.round(dst[0] + (hash(i, seed) - 0.5) * 14), Math.round(dst[1] + (hash(i, seed + 1) - 0.5) * 12), C('white', 8));
      continue;
    }
    const off = (hash(i, seed) - 0.5) * 16 * (1 - k * 0.3);
    const p: V = [hand[0] + (dst[0] - hand[0]) * k, hand[1] + (dst[1] - hand[1]) * k + off * (1 - k)];
    const q: V = [p[0] - s.d[0] * 5, p[1] - s.d[1] * 3];
    level(g, 3);
    line(g, q, p, i % 2 ? C(P.a, 5) : C('white', 7));
    g.globalAlpha = 1;
  }
}

function one(art: string, P: Pal, wpn: Wpn, [skill, fam, reach]: HandRow, idx: number): ArtFx {
  const id = `${art}-${skill}`;
  const seed = idx * 7 + 5;
  const base = { id, skill, th: skill, on: true, weapon: wpn, note: NOTE[fam] };
  switch (fam) {
    case 'palm': {
      const D = reach && reach >= 4 ? Math.min(110, tilesPx(reach) * 0.6) : 0;
      const hit = 0.62;
      return {
        ...base,
        acts: [{ m: 'wxPalm', t0: 0.15, t1: 1.2 }],
        hits: [hit],
        big: hit,
        ghosts: D ? [0.5, 0.55, 0.6] : undefined,
        pre: 0.45,
        hit,
        end: 1.6,
        dash: D || undefined,
        afterimage: D ? 'dash' : null,
        move: D ? dashMove(D, 0.45) : undefined,
        under(g, s) {
          if (D) puffs(g, s.a, since(s.t, 0.42), 6, 3, 2, 'brown', 6, 0.3, seed);
          const e = since(s.t, hit);
          if (e >= 0 && e < 10) {
            level(g, fade(e, 4, 8));
            isoRing(g, s.f[0], s.f[1], 6 + Math.round(e * 2.2), C(P.b, 5), true);
            g.globalAlpha = 1;
          }
        },
        over(g, s) {
          handGlow(g, s, 0.25, 0.55, P);
          landing(g, s, hit, P, seed, 1.1);
        },
      };
    }
    case 'fist': {
      const hit = 0.6;
      return {
        ...base,
        acts: [{ m: 'wxSlam', t0: 0.15, t1: 1.1 }],
        hits: [hit],
        big: hit,
        pre: 0.4,
        hit,
        end: 1.5,
        afterimage: null,
        under(g, s) {
          puffs(g, s.f, since(s.t, hit), 6, 4, 2, 'brown', 6, 0.2, seed);
        },
        over(g, s) {
          handGlow(g, s, 0.25, 0.55, P);
          landing(g, s, hit, P, seed, 1.5, false);
          const e = since(s.t, hit);
          const c = strikeAt(s);
          if (e >= 0 && e < 6) {
            level(g, fade(e, 2, 5));
            for (let i = 0; i < 8; i++) {
              const a = (i / 8) * Math.PI * 2;
              const r0 = 3 + e * 2;
              line(g, [c[0] + Math.cos(a) * r0, c[1] + Math.sin(a) * r0 * 0.65], [c[0] + Math.cos(a) * (r0 + 5), c[1] + Math.sin(a) * (r0 + 5) * 0.65], i % 2 ? C(P.a, 5) : C('white', 7));
            }
            g.globalAlpha = 1;
          }
        },
      };
    }
    case 'claw': {
      return {
        ...base,
        acts: [
          { m: 'wxCleave', t0: 0.2, t1: 0.55 },
          { m: 'wxCleave', t0: 0.6, t1: 0.95 },
        ],
        hits: [0.45, 0.8],
        big: 0.8,
        ghosts: [0.38, 0.68],
        stepIn: 6,
        pre: 0.3,
        hit: 0.45,
        end: 1.6,
        afterimage: 'port',
        over(g, s) {
          handGlow(g, s, 0.25, 0.4, P);
          for (const [at, sd] of [[0.45, 0], [0.8, 1]] as const) {
            const e = since(s.t, at);
            const c = strikeAt(s);
            if (e >= 0 && e < 8) {
              level(g, fade(e, 3, 7));
              for (let k = -1; k <= 1; k++) {
                const o = k * 4;
                line(g, [c[0] - 8 + o, c[1] - 9 + sd * 3], [c[0] + 8 + o, c[1] + 9 + sd * 3], k ? C(P.a, 5) : C('white', 8));
              }
              g.globalAlpha = 1;
            }
            landing(g, s, at, P, seed + sd, 0.9, false);
          }
        },
      };
    }
    case 'grab': {
      return {
        ...base,
        acts: [{ m: 'wxThrust', t0: 0.25, t1: 1.1 }],
        hits: [0.6],
        big: 0.6,
        ghosts: [0.46, 0.52, 0.58],
        stepIn: 7,
        pre: 0.4,
        hit: 0.6,
        end: 1.7,
        afterimage: 'port',
        under(g, s) {
          const t = since(s.t, 0.2);
          if (t >= 0 && s.t < 0.7) {
            level(g, 3);
            ringOn(g, s.a, 12, -Math.PI / 2, clamp01(t / 5), C(P.b, 4), true);
            g.globalAlpha = 1;
          }
        },
        over(g, s) {
          handGlow(g, s, 0.3, 0.55, P);
          const e = since(s.t, 0.6);
          const c = strikeAt(s);
          if (e >= 0 && e < 14) {
            level(g, fade(e, 8, 13));
            const r = Math.round(9 - Math.min(e, 4));
            for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
              const x = c[0] + sx * r;
              const y = c[1] + sy * r * 0.7;
              line(g, [x, y], [x - sx * 4, y], C(P.a, 6));
              line(g, [x, y], [x, y - sy * 3], C(P.a, 6));
            }
            g.globalAlpha = 1;
          }
          landing(g, s, 0.6, P, seed, 1.1, false);
        },
      };
    }
    case 'throw': {
      const n = Math.min(9, 3 + Math.round((reach ?? 5) / 2));
      return {
        ...base,
        acts: [{ m: 'wxQi', t0: 0.25, t1: 1.1 }],
        hits: [0.75],
        pre: 0.35,
        hit: 0.75,
        end: 1.6,
        afterimage: null,
        over(g, s) {
          handGlow(g, s, 0.3, 0.5, P);
          darts(g, s, 0.5, 0.75, P, seed, n);
          landing(g, s, 0.75, P, seed, 0.9, false);
        },
      };
    }
    case 'ring': {
      const rx = Math.min(112, tilesPx(reach ?? 3.5));
      const hit = 0.7;
      return {
        ...base,
        acts: [
          { m: 'wxStomp', t0: 0.15, t1: 1.1 },
        ],
        hits: [hit],
        big: hit,
        ghosts: [0.6, 0.66],
        stepIn: 4,
        pre: 0.45,
        hit,
        end: 1.8,
        afterimage: 'port',
        under(g, s) {
          ringBurst(g, s, hit, rx, P, seed);
        },
        over(g, s) {
          handGlow(g, s, 0.3, 0.55, P);
          bladeArc(g, s, 0.5, 0.9, P.a, 2, 7);
        },
      };
    }
    case 'power': {
      const rx = Math.min(100, tilesPx(reach ?? 3.5));
      return {
        ...base,
        acts: [
          { m: 'wxPower', t0: 0.05, t1: 1.0 },
          { m: 'wxPalm', t0: 1.1, t1: 1.5 },
        ],
        hits: [1.2],
        big: 1.2,
        ghosts: [1.0, 1.08, 1.16],
        cast: [0.1, 1.0],
        pre: 0.9,
        hit: 1.2,
        end: 2.4,
        afterimage: 'port',
        under(g, s) {
          ringBurst(g, s, 1.2, rx, P, seed);
          const w = since(s.t, 0.1);
          if (w >= 0 && s.t < 1.1) puffs(g, s.a, w % 8, 4, 12, -1.2, P.b, 8, 0, seed);
        },
        over(g, s) {
          handGlow(g, s, 0.7, 1.1, P);
          landing(g, s, 1.2, P, seed, 1.7);
        },
      };
    }
    case 'buff':
    case 'heal': {
      const heal = fam === 'heal';
      return {
        ...base,
        acts: [{ m: heal ? 'wxHeal' : 'wxBuff', t0: 0.1, t1: 2.0 }],
        hits: [],
        pre: 0.1,
        hit: 0.9,
        end: 2.4,
        afterimage: null,
        under(g, s) {
          const e = since(s.t, 0.1);
          if (e < 0 || e > 26) return;
          level(g, fade(e, 14, 20));
          isoRing(g, s.a[0], s.a[1], 10 + Math.min(e, 8), C(P.b, 5), true);
          g.globalAlpha = 1;
        },
        over(g, s) {
          const e = since(s.t, 0.1);
          if (e < 0 || e > 26) return;
          const h = s.chest * 1.6;
          for (let i = 0; i < 3; i++) {
            const k = (e - i * 4) / 12;
            if (k < 0 || k > 1) continue;
            level(g, k < 0.7 ? 3 : 2);
            isoRing(g, s.a[0], s.a[1] - k * h, 9, i % 2 ? C(P.b, 5) : C(P.a, 6), i === 0);
          }
          g.globalAlpha = 1;
          if (e < 22) for (let i = 0; i < 5; i++) {
            level(g, 3);
            px(g, s.a[0] + (hash(i, seed) - 0.5) * 18, s.a[1] - ((e * 2 + i * 9) % (h + 6)), i % 2 ? C('white', 8) : C(P.a, 5));
          }
          g.globalAlpha = 1;
          if (!heal) motif(g, [s.a[0], s.a[1] - s.chest], e - 2, P, seed, 5, 0.6, 14);
        },
      };
    }
  }
}

export function makeHandArt(art: string, P: Pal, rows: readonly HandRow[], wpn: Wpn = 'none'): readonly ArtFx[] {
  return rows.map((r, i) => one(art, P, wpn, r, i));
}
